import { DepartmentMemberOrgEnum } from '@/components/MemberSelectModal/interface';
import { request } from '@umijs/max';
import type {
  CreateWorkSpaceReq,
  FetchWorkspaceTasksReq,
  FetchWorkspaceTasksRes,
  GetCollaboratorsAvatarRes,
  GetCollaboratorsRes,
  GetWorkspaceInfoRes,
  ITag,
  ITaskItem,
  ITemplate,
  IUserSharingTree,
  PutSharingReq,
  nodeData,
} from './type';

export const getUserTags = () => {
  return request<IRes<ITag[]>>('/supertext-uic/users/tags');
};

export const createUserTags = (tagIds: number[]) => {
  return request<IRes<string>>('/supertext-uic/users/tags', {
    method: 'POST',
    data: {
      tag_ids: tagIds,
    },
  });
};

/**
 * getTaskList
 * @param params { template_code: string, only_shared: 'true' | 'false' }
 * @returns Promise<IRes<IList<ITaskItem>>>
 */
export const getTaskList = (
  pageNumber: number,
  params?: Partial<{
    template_code: string;
    task_codes: string;
    only_shared: boolean;
    need_handle: boolean;
  }>,
) => {
  return request<IRes<IList<ITaskItem>>>('/supertext-java/tasks/', {
    params: {
      page: pageNumber,
      size: 20,
      ...params,
    },
  });
};

export const deleteTask = (taskCode: string) => {
  return request(`/supertext-java/tasks/${taskCode}`, {
    method: 'DELETE',
  });
};

/**
 * 查找分享用户树
 * @param resource_id 资源id
 * @param dept_uid 部门uid
 * @param keyword  关键词
 */
export const getUserSharingTree = (params: {
  resource_code?: string | number;
  resource_type?: number;
  dept_uid?: string;
  keyword?: string;
}) => {
  return request<IRes<IUserSharingTree>>(`/supertext-java/sharing/user/tree`, {
    params,
  });
};

/**
 * 分享接口
 */
export const putSharing = (params: PutSharingReq) => {
  return request<IRes<string>>('/supertext-java/sharing', {
    method: 'PUT',
    data: params,
  });
};
/**
 * 取消接口
 */
export const deleteSharing = (params: PutSharingReq) => {
  return request<IRes<string>>('/supertext-java/sharing', {
    method: 'DELETE',
    data: params,
  });
};

/**
 * 新增工作空间
 */
export const createWorkspace = async (params: CreateWorkSpaceReq) => {
  const data = await request<
    IRes<{
      workspace_id: number;
      workspace_uid: string;
    }>
  >('/supertext-java/workspace', {
    method: 'PUT',
    data: params,
  });
  if (data.code === 0) return data.data;
  return null;
};
/**
 * 更新工作空间
 */
export const updateWorkspace = async (params: CreateWorkSpaceReq) => {
  return await request<IRes<string>>('/supertext-java/workspace', {
    method: 'POST',
    data: params,
  });
};

/**
 * 获取协作者头像
 */
export const getCollaboratorsAvatar = async (params: {
  resource_code?: string;
  resource_type: number | string;
}) => {
  const res = await request<IRes<GetCollaboratorsAvatarRes>>(
    '/supertext-java/sharing/collaborators/avatars',
    {
      params,
    },
  );
  if (res.code !== 0) return null;
  return res.data;
};

/**
 * 获取协作者
 * TASK(0, "任务"),     TEMPLATE(1, "模板"),     MENU(2, "菜单"),     INTERFACE(3, "接口"),     TASK_BATCH(4, "任务批次");
 */
export const getCollaborators = async (params: {
  resource_code?: string;
  resource_type: number;
}) => {
  const res = await request<IRes<GetCollaboratorsRes[]>>(
    '/supertext-java/sharing/collaborators',
    {
      params,
    },
  );
  if (res.code !== 0) return null;

  const TypeMap = {
    DEPARTMENT: DepartmentMemberOrgEnum.DEPARTMENT,
    PERSON: DepartmentMemberOrgEnum.USER,
    ORGANIZATION: DepartmentMemberOrgEnum.ORG,
  };

  const validData = res.data.map((item) => {
    return {
      ...item,
      // @ts-ignore
      entity_type: TypeMap[item.entity_type],
    };
  });

  return validData;
};

export const getFavoriteTemplateList = (page: number) => {
  return request<IRes<IList<ITemplate>>>(
    '/supertext-java/favorites/templates',
    {
      page,
      size: 50,
    },
  );
};

export const deleteFavoriteTemplate = (templateCode: string) => {
  return request<IRes<any>>(
    `/supertext-java/favorites/templates/${templateCode}`,
    {
      method: 'DELETE',
    },
  );
};

export const getTemplateDetail = (
  templateCode: string,
  signal?: AbortSignal,
) => {
  return request<IRes<any>>(`/supertext-java/templates/${templateCode}`, {
    signal,
  });
};
export const deleteWorkspace = (id: string) => {
  return request<IRes<string>>(`/supertext-java/workspace/${id}`, {
    method: 'DELETE',
  });
};
export const getWorkspaceInfo = async (id: string) => {
  const res = await request<IRes<GetWorkspaceInfoRes>>(
    `/supertext-java/workspace/${id}`,
    {
      method: 'GET',
    },
  );
  if (res.code !== 0) return null;
  return res.data;
};

/**
 * 删除工作空间任务
 */

export const deleteWorkspaceTask = ({
  workspace_uid,
  task_codes,
}: {
  workspace_uid: string;
  task_codes: string;
}) => {
  return request<IRes<string>>(
    `/supertext-java/workspace/tasks/${task_codes}`,
    {
      method: 'DELETE',
      params: {
        workspace_uid,
      },
    },
  );
};

/**
 * 获取工作空间tasks列表
 * /supertext-java/workspace/tasks
 */
export const fetchWorkspaceTasks = (params: FetchWorkspaceTasksReq) => {
  return request<IRes<IList<FetchWorkspaceTasksRes>>>(
    '/supertext-java/workspace/tasks',
    { params },
  );
};

/**
 * 从工作空间根据模板code查询节点负责人角色
 */
export const fetchWorkspaceTaskRoles = (params: { template_code: string }) => {
  return request<IRes<nodeData>>('/supertext-java/flow/node/head/role', {
    params,
  });
};
export type * from './type';
